import { Box, ClickAwayListener, Tooltip, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { useTranslation } from 'react-i18next';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useState } from 'react';

import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { Action, AssetTypeEnum, PluralEnum, Subject } from '@/types/enums';
import { getIconByAssetType } from '@/utils';
import { Asset, checkAssetTypeValue } from '@/types/asset_search';
import { setAsset } from '@/features/asset/assetSlice';
import { InfoIcon } from './Icons';
import { ClientConfig } from '@/types/types';
import { RootState } from '@/app/store';
import Can from '@/app/Auth/Can';
import BatteryLevel from './BatteryLevel';
import { ORG_MANHEIM_CODE } from '@/constants';

export function AssetPanelContent({
  row: rowProp,
  handleDataTableMobile,
  handleAssetSelected,
}: {
  row: Asset;
  handleDataTableMobile: any;
  handleAssetSelected: any;
}) {
  const apiRef = useGridApiContext();
  const [open, setOpen] = useState(false);
  const siteId = useAppSelector((state: RootState) => state.user.siteId);
  const nameSite = useAppSelector((state) => state.user.siteMetadata.name);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [width, setWidth] = useState(() => {
    const dimensions = apiRef.current.getRootDimensions();
    return dimensions?.viewportInnerSize.width;
  });

  const siteMetadata: ClientConfig = useAppSelector(
    (state: any) => state.user.siteMetadata,
  );
  const handleViewportInnerSizeChange = useCallback(() => {
    const dimensions = apiRef.current.getRootDimensions();
    setWidth(dimensions?.viewportInnerSize.width);
  }, [apiRef]);

  useEffect(() => {
    return apiRef.current.subscribeEvent(
      'viewportInnerSizeChange',
      handleViewportInnerSizeChange,
    );
  }, [apiRef, handleViewportInnerSizeChange]);
  const { t } = useTranslation();
  const I18nMap = new Map<keyof Asset, string>();

  I18nMap.set(
    'asset_uuid',
    t(`vin.${siteMetadata.assetType}`, {
      count: PluralEnum.SINGULAR,
    }),
  );
  I18nMap.set('zone_name', t('assetDetail.zone'));

  if (siteMetadata.assetType === AssetTypeEnum.CAR) {
    I18nMap.set('vehicle_stock_number', t('assetDetail.stockNumber'));
    if (
      Can(Action.READ, Subject.table_dtc) &&
      rowProp &&
      rowProp['dtc'] &&
      rowProp['dtc'].length > 0
    ) {
      I18nMap.set('dtc', t('assetDetail.dtcCodes'));
    }
    if (Can(Action.READ, Subject.vehicle_battery)) {
      I18nMap.set('vehicle_level', t('assetDetail.battery'));
    }
    I18nMap.set('vehicle_make', t('assetDetail.make'));
    I18nMap.set(
      rowProp.vehicle_model ? 'vehicle_model' : 'vehicle_model_code',
      t('assetDetail.model'),
    );
    I18nMap.set('vehicle_year', t('assetDetail.year'));
    if (Can(Action.READ, Subject.seller_id)) {
      I18nMap.set('seller_id', t('assetDetail.sellerId'));
    }
    if (siteMetadata.orgCode == ORG_MANHEIM_CODE) {
      I18nMap.set('sale_year', t('assetDetail.saleYear'));
      I18nMap.set('sale_number', t('assetDetail.saleNumber'));
      I18nMap.set('sale_lane', t('assetDetail.saleLane'));
      I18nMap.set('sale_run', t('assetDetail.saleRun'));
    }
  }
  I18nMap.set('assoc_timestamp', t('assetDetail.associationTimestamp'));
  I18nMap.set('time_in_zone', t('assetDetail.timeInZone'));

  const fieldsToShow = new Map(
    [...I18nMap.entries()].filter(
      (asset) => rowProp && rowProp[asset[0] as keyof Asset],
    ),
  );

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  return (
    <Box data-testid="detail-panel">
      <Box
        sx={(theme) => ({
          padding: '20px 16px',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          py: 2,
          height: '100%',
          boxSizing: 'border-box',
          position: 'sticky',
          left: 0,
          background: theme.palette.background.paper,
          width,
        })}
      >
        {ComponentGenerator(getIconByAssetType(siteMetadata.assetType))}

        <Typography
          sx={(theme) => ({
            flex: 1,
            fontSize: '12px',
            fontWeight: '600',
            color: theme.palette.text.primary,
          })}
        >
          {t(`assetType.${siteMetadata.assetType}`, {
            count: PluralEnum.SINGULAR,
          })}
        </Typography>
      </Box>
      <Box
        sx={{
          padding: '20px 16px',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          py: 2,
          height: '100%',
          boxSizing: 'border-box',
          position: 'sticky',
          left: 0,
          width,
        }}
        onClick={() => {
          dispatch(setAsset(rowProp));
          handleAssetSelected(rowProp);
          handleDataTableMobile(false);
        }}
      >
        <Typography
          sx={(theme) => ({
            flex: 1,
            fontSize: '14px',
            fontWeight: '600',
            color: theme.palette.text.primary,
          })}
          data-testid={`view-map-${rowProp.asset_uuid}`}
          className="cell-link table-cell-trucate"
        >
          {t(`mobilePanel.viewMap`)}
          <OpenInNewIcon
            sx={() => ({
              fontSize: '15px',
            })}
          />
        </Typography>
      </Box>
      {Array.from(fieldsToShow.keys(), (asset, i) => (
        <Box
          key={`${asset}-${i}`}
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            background:
              i % 2 == 0
                ? theme.palette.background.paper
                : theme.palette.elevation01,
            padding: '12px 16px',
            textAlign: 'left',
            py: 2,
            height: '100%',
            boxSizing: 'border-box',
            position: 'sticky',
            left: 0,
            width,
          })}
        >
          <Typography
            sx={(theme) => ({
              flex: 1,
              fontSize: '12px',
              fontWeight: '600',
              color: theme.palette.text.primary,
            })}
            data-testid={`asset-detail-label-${asset}`}
          >
            {I18nMap.get(asset as keyof Asset)}
            {(asset as keyof Asset) === 'dtc' &&
              Can(Action.READ, Subject.disclaimer) && (
              <ClickAwayListener onClickAway={handleTooltipClose}>
                <Tooltip
                  onClose={handleTooltipClose}
                  open={open}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  placement="bottom-end"
                  data-testid="dtc-disclaimer"
                  title={
                    <>
                      <Typography
                        sx={() => ({
                          fontSize: '14px',
                          fontWeight: '600',
                          width: '100px',
                        })}
                      >
                        {t('disclaimerDialog.dtcDisclaimerTitle')}
                        <br />
                        <br />
                      </Typography>
                      <Typography
                        sx={() => ({
                          fontSize: '14px',
                        })}
                      >
                        {t('disclaimerDialog.dtcDisclaimer', {
                          SITE_ID: nameSite,
                        })}
                      </Typography>
                    </>
                  }
                >
                  <span onClick={handleTooltipOpen}>
                    <InfoIcon
                      style={{
                        width: '16px',
                        height: '22px',
                        marginLeft: '10px',
                      }}
                      viewBox="0 0 20 20"
                    />
                  </span>
                </Tooltip>
              </ClickAwayListener>
            )}
          </Typography>
          {(asset as keyof Asset) === 'vehicle_level' ? (
            <Typography
              sx={(theme) => ({
                flex: 1,
                fontSize: '12px',
                color: theme.palette.text.primary,
              })}
              data-testid={`asset-detail-value-${asset}`}
            >
              <BatteryLevel asset={rowProp} />
            </Typography>
          ) : (
            <Typography
              sx={(theme) => ({
                flex: 1,
                fontSize: '12px',
                color: theme.palette.text.primary,
                wordWrap: 'break-word',
                maxWidth: '70%',
              })}
              data-testid={`asset-detail-value-${asset}`}
            >
              {checkAssetTypeValue(
                asset as keyof Asset,
                rowProp[asset as keyof Asset],
                t,
              )}
            </Typography>
          )}
        </Box>
      ))}
      {siteMetadata.orgCode !== ORG_MANHEIM_CODE && (
        <Box
          sx={{
            padding: '20px 16px',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            py: 2,
            height: '100%',
            boxSizing: 'border-box',
            position: 'sticky',
            left: 0,
            width,
          }}
          onClick={() => {
            dispatch(setAsset(rowProp));
            navigate(`/${siteId}/assets/${rowProp.asset_uuid}`);
          }}
        >
          <Typography
            sx={(theme) => ({
              flex: 1,
              fontSize: '14px',
              fontWeight: '600',
              color: theme.palette.text.primary,
            })}
            data-testid={`more-detail-${rowProp.asset_uuid}`}
            className="cell-link table-cell-trucate"
          >
            {t(`mobilePanel.moreDetails`)}
            <OpenInNewIcon
              sx={() => ({
                fontSize: '15px',
              })}
            />
          </Typography>
        </Box>
      )}
    </Box>
  );
}

const ComponentGenerator = (WrappedComponent: any) => {
  return (
    <WrappedComponent
      style={{
        width: '18px',
        height: '18px',
        marginRight: '20px',
      }}
      viewBox="0 -2 20 24"
    />
  );
};
